import {
  interfaceUrlAndNameAtomFamily_SUSPENDS,
  nodeAtomFamily,
  NodeType,
  AssetType,
  previewNodeIdAtom,
  isDefaultNode,
  assetAtomFamily_SUSPENDS,
  determineAssetType,
  getAssetId,
  getTablesOrigin,
  DefaultNodeData as NewDefaultNodeData,
  isFocusModeEnabledAtomFamily,
} from "@zapier/canvas-state";
import { useAtomValue, useSetAtom } from "jotai";
import { Suspense, useEffect } from "react";

import { ModalContainer } from "./ModalContainer";
import { FocusModeContainer } from "./FocusModeContainer";

function getAssetTitle(asset: any, assetType: AssetType): string {
  if (!asset) return `${assetType} - Loading...`;

  switch (assetType) {
    case "zap":
      return asset.title || `Untitled ${assetType}`;
    case "table":
    case "chatbot":
    case "interface":
      return asset.name || `Untitled ${assetType}`;
    default:
      return `${assetType} - ${asset.id || "Unknown"}`;
  }
}

function SuspendedComponent({ previewNodeId }: { previewNodeId: string }) {
  const setPreviewNodeIdAtom = useSetAtom(previewNodeIdAtom);
  const node = useAtomValue(nodeAtomFamily(previewNodeId));
  const assetType = determineAssetType(
    (node.data as any).services,
    node.type as NodeType,
  );
  const selectedAssetId = getAssetId(node.data, assetType);
  const asset = useAtomValue(
    assetAtomFamily_SUSPENDS({
      assetType,
      assetId: selectedAssetId,
      interfaceProjectId: (node.data as NewDefaultNodeData).projectId,
    }),
  );
  const url = getUrl(asset, assetType, selectedAssetId);

  const isFocusModeEnabled = useAtomValue(
    isFocusModeEnabledAtomFamily(assetType),
  );

  const Component = isFocusModeEnabled ? FocusModeContainer : ModalContainer;

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setPreviewNodeIdAtom(null);
      }
    };
    window.addEventListener("keydown", handleEscape);

    return () => window.removeEventListener("keydown", handleEscape);
  }, [setPreviewNodeIdAtom]);

  const handleClose = () => {
    setPreviewNodeIdAtom(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (
    assetType === "interface" &&
    isDefaultNode(node) &&
    node.data.projectId &&
    node.data.pageId
  ) {
    return (
      <InterfaceDetailModal
        interfaceProjectId={node.data.projectId}
        interfacePageId={node.data.pageId}
        previewNodeId={previewNodeId}
      />
    );
  }

  return (
    <Component
      onOverlayClick={handleOverlayClick}
      title={
        isFocusModeEnabled
          ? getAssetTitle(asset, assetType)
          : `Asset Detail Modal: ${assetType} - ${selectedAssetId}`
      }
      url={url}
      assetType={assetType}
      // TODO check type of asset to make sure its not error type
      // disableIframe={assetType === "zap" && !asset?.is_published}
      // For now we will disable for all asset types
      disableIframe={true}
      onClose={() => {
        setPreviewNodeIdAtom(null);
      }}
    />
  );
}

export function AssetDetailModal({ previewNodeId }: { previewNodeId: string }) {
  return (
    <Suspense fallback={null}>
      <SuspendedComponent previewNodeId={previewNodeId} />
    </Suspense>
  );
}

function getUrl(asset: any, assetType: AssetType, id: string) {
  if (assetType === "zap") {
    if (asset.is_published) {
      return `/editor/${id}/published?is_view_mode=true`;
    } else {
      return `/editor/${id}/draft`;
    }
  }
  if (assetType === "table") {
    return `https://${getTablesOrigin()}/app/tables/t/${id}`;
  }

  if (assetType === "chatbot") {
    return `/app/chatbots/${id}`;
  }
}

function InterfaceDetailModal({
  interfaceProjectId,
  interfacePageId,
  previewNodeId,
}: {
  interfaceProjectId: string;
  interfacePageId: string;
  previewNodeId: string;
}) {
  const setPreviewNodeId = useSetAtom(previewNodeIdAtom);
  const isFocusModeEnabled = useAtomValue(
    isFocusModeEnabledAtomFamily("interface"),
  );

  const { url: interfaceUrl, projectName } = useAtomValue(
    interfaceUrlAndNameAtomFamily_SUSPENDS({
      projectId: interfaceProjectId,
      pageId: interfacePageId,
      isFocusModeEnabled,
      nodeId: previewNodeId,
    }),
  );

  const Component = isFocusModeEnabled ? FocusModeContainer : ModalContainer;

  const handleClose = () => {
    setPreviewNodeId(null);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <Component
      onOverlayClick={handleOverlayClick}
      title={
        isFocusModeEnabled
          ? getAssetTitle(projectName, "interface")
          : `Asset Detail Modal: ${projectName || "Interface"}`
      }
      url={interfaceUrl || undefined}
      assetType="interface"
      disableIframe={true}
      onClose={handleClose}
    />
  );
}
